/**
 * Ads-pixel + third-party analytics wrappers.
 *
 * All firing is env-gated so this is safe to call even when tags aren't
 * configured. Set these in /app/frontend/.env to activate:
 *   - REACT_APP_META_PIXEL_ID    — Meta (Facebook) Pixel ID (e.g. "1234567890")
 *   - REACT_APP_GOOGLE_ADS_ID    — Google Ads conversion ID (e.g. "AW-12345")
 *   - REACT_APP_GOOGLE_ADS_LABEL — Conversion label for paid events
 *
 * Each pixel is lazy-loaded on first `initPixels()` call so cold-load latency
 * on /rate-my-cv isn't affected when there are no configured tags.
 */

const META_ID = process.env.REACT_APP_META_PIXEL_ID || "";
const GADS_ID = process.env.REACT_APP_GOOGLE_ADS_ID || "";
const GADS_LABEL = process.env.REACT_APP_GOOGLE_ADS_LABEL || "";

let _initialized = false;

export function initPixels() {
    if (_initialized || typeof window === "undefined") return;
    _initialized = true;

    // Meta Pixel loader — official snippet, trimmed.
    if (META_ID) {
        (function (f, b, e, v, n, t, s) {
            if (f.fbq) return;
            n = f.fbq = function () {
                n.callMethod ? n.callMethod.apply(n, arguments) : n.queue.push(arguments);
            };
            if (!f._fbq) f._fbq = n;
            n.push = n; n.loaded = !0; n.version = "2.0"; n.queue = [];
            t = b.createElement(e); t.async = !0; t.src = v;
            s = b.getElementsByTagName(e)[0]; s.parentNode.insertBefore(t, s);
        })(window, document, "script", "https://connect.facebook.net/en_US/fbevents.js");
        window.fbq("init", META_ID);
        window.fbq("track", "PageView");
    }

    // Google Ads / gtag loader — reused for both AW-* and GA4 conversion tags.
    if (GADS_ID) {
        const s = document.createElement("script");
        s.async = true;
        s.src = `https://www.googletagmanager.com/gtag/js?id=${GADS_ID}`;
        document.head.appendChild(s);
        window.dataLayer = window.dataLayer || [];
        window.gtag = function () { window.dataLayer.push(arguments); };
        window.gtag("js", new Date());
        window.gtag("config", GADS_ID);
    }
}

// Map internal funnel events to platform-specific standard events.
const META_STANDARD = {
    landing_view: "ViewContent",
    upload_started: "Lead",
    signup_prompted: "InitiateCheckout",
    rating_shown: "CompleteRegistration",
    rewrite_free_click: "AddToCart",
    rewrite_paid_click: "InitiateCheckout",
    checkout_completed: "Purchase",
    pro_subscribe_click: "InitiateCheckout",
    pro_subscribed: "Purchase",
    linkedin_analyze: "Search",
};

const CONVERSION_EVENTS = new Set(["checkout_completed", "pro_subscribed"]);

/**
 * Fire a funnel event to Meta + Google Ads if configured. Always safe.
 * @param {string} event  — canonical funnel event name (matches backend whitelist)
 * @param {object} meta   — arbitrary payload (score, band, etc.)
 */
export function pixelTrack(event, meta = {}) {
    if (typeof window === "undefined") return;
    // Meta Pixel
    if (META_ID && window.fbq) {
        const standard = META_STANDARD[event];
        if (standard) {
            const params = {};
            if (event === "checkout_completed") {
                params.value = 299; params.currency = "INR";
            } else if (event === "pro_subscribed") {
                params.value = 499; params.currency = "INR";
            }
            try { window.fbq("track", standard, params); } catch (_) { /* silent */ }
        }
        // Also fire custom event so analytics can slice by NaukriDaddy-specific name
        try { window.fbq("trackCustom", `cv_${event}`, meta || {}); } catch (_) { /* silent */ }
    }
    // Google Ads
    if (GADS_ID && window.gtag) {
        try {
            window.gtag("event", `cv_${event}`, {
                ...(meta || {}),
                send_to: GADS_ID,
            });
            if (CONVERSION_EVENTS.has(event) && GADS_LABEL) {
                window.gtag("event", "conversion", {
                    send_to: `${GADS_ID}/${GADS_LABEL}`,
                    value: event === "pro_subscribed" ? 499 : 299,
                    currency: "INR",
                });
            }
        } catch (_) { /* silent */ }
    }
}
